import { useState, useEffect, useRef } from 'react';
import { doc, onSnapshot, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../lib/AuthContext';
import { Modal } from './Tasks';
import MoodBoard from '../components/MoodBoard';
import { Plus, Edit2, ExternalLink, Check, X, Flame } from 'lucide-react';

// ── Moon ─────────────────────────────────────────────────────
function getMoonData() {
  const refNewMoon = new Date('2025-01-29T00:00:00Z');
  const now = new Date();
  const daysSinceRef = (now - refNewMoon) / (1000 * 60 * 60 * 24);
  const cycleLength = 29.53059;
  const dayInCycle = ((daysSinceRef % cycleLength) + cycleLength) % cycleLength;
  const pct = dayInCycle / cycleLength;
  const daysToFull = dayInCycle < 14.77 ? 14.77 - dayInCycle : cycleLength - dayInCycle + 14.77;
  const daysToNew = dayInCycle > 0.5 ? cycleLength - dayInCycle : -dayInCycle;
  const nextCast = daysToFull < daysToNew
    ? { name: 'Full Moon 🌕', days: Math.ceil(daysToFull), type: 'full' }
    : { name: 'New Moon 🌑', days: Math.ceil(Math.abs(daysToNew)), type: 'new' };
  const phase = pct < 0.02 ? '🌑' : pct < 0.25 ? '🌒' : pct < 0.48 ? '🌓' : pct < 0.52 ? '🌕' : pct < 0.75 ? '🌖' : pct < 0.98 ? '🌘' : '🌑';
  return { nextCast, phase, dayInCycle: Math.floor(dayInCycle), cycleLength: 30 };
}

// ── Data ──────────────────────────────────────────────────────
const NODES = [
  { id: 'n1', num: '01', name: 'Music',                color: '#e8621a', milestone: '2 Yr — 1M Monthly Listeners',         intention: 'Daily, consistent, integrated vocal practice. The body becomes the instrument. The instrument becomes the channel.' },
  { id: 'n2', num: '02', name: 'Gonzo Expeditions',    color: '#7b4fc4', milestone: '2 Yr — Quarterly · Fully Funded',      intention: 'Fully funded, consistent quarterly expeditions through resonant brand sponsorship. Freedom of movement and access as a direct consequence of the work.' },
  { id: 'n3', num: '03', name: 'Financial Abundance',  color: '#c98a18', milestone: '6 Mo — Debt Free · 3k/mo',             intention: 'In 6 months: debt free. 3k/month steady through AhziRa this year. Freedom and flourishing as direct consequence of the work.' },
  { id: 'n4', num: '04', name: 'Audience & Community', color: '#1a9080', milestone: '1 Yr — 100k · 2 Yr — 1M',             intention: 'A community exists to receive, be nourished by, and expand AhziRa. Active receptors in the world. People genuinely transformed by contact with the work.' },
  { id: 'n5', num: '05', name: 'Good Love',            color: '#c0392b', milestone: 'Goddess · Cat · Fairy · Witch',        intention: 'I can do what I do better with you. We share this mission. Two parts of the same force. You get me. You love me. You know me. You see me.' },
];

const TIMELINE = [
  { label: '3 Months',  color: '#e8621a', items: ['New Sigil is live', '1k active monthlies'] },
  { label: '6 Months',  color: '#7b4fc4', items: ['Debt free', '3k/month steady', 'Singularity Substance complete', 'Consistent Sigil single releases'] },
  { label: '1 Year',    color: '#1a9080', items: ['100k active receptors', 'Album complete and fully dispersed'] },
  { label: '2 Years',   color: '#a87ee8', items: ['1 million active receptors', 'AhziRa Expeditions — quarterly, fully funded', 'Hypersigil 1 complete'] },
];

const STATUS_META = {
  cast:     { label: 'Cast ✦',      color: '#27ae60' },
  charging: { label: 'Charging ⚡',  color: '#e8621a' },
  upcoming: { label: 'Upcoming 🌙', color: '#7b4fc4' },
  forming:  { label: 'Forming 🌱',  color: '#c98a18' },
};

const DEFAULT_DATA = {
  canvases: {},
  sigils: NODES.map(n => ({ nodeId: n.id, song: '', sigilUrl: '', charged: false })),
  singularityUrl: '',
  chargeLog: [],
  releases: [
    { id: 'r1', title: 'Gravity',        type: 'single', status: 'cast',     castDate: '2025-03-01', nodeId: 'n1' },
    { id: 'r2', title: 'Plane Site',     type: 'single', status: 'charging', castDate: '',           nodeId: 'n1' },
    { id: 'r3', title: 'Planet X EP',    type: 'ep',     status: 'upcoming', castDate: '',           nodeId: '' },
    { id: 'r4', title: 'Forbiddhen Love',type: 'album',  status: 'forming',  castDate: '',           nodeId: '' },
  ],
};

// ── Firestore ─────────────────────────────────────────────────
function useHypersigil(artistId) {
  const [data, setData] = useState(null);
  const docId = artistId || 'ahzira';
  const ref = doc(db, 'hypersigil', docId);
  useEffect(() => {
    return onSnapshot(ref, snap => {
      if (snap.exists()) { setData(snap.data()); }
      else { setDoc(ref, { ...DEFAULT_DATA, artistId: docId, isPrivate: false, updatedAt: serverTimestamp() }); setData({ ...DEFAULT_DATA, artistId: docId, isPrivate: false }); }
    });
  }, [docId]);
  const update = (updates) => updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
  return { data, update };
}

// ── Always-On Header ──────────────────────────────────────────
function IntentionsDashboard({ data, update }) {
  const moon = getMoonData();
  const [expandedNode, setExpandedNode] = useState(null);
  const [editingNode, setEditingNode] = useState(null);
  const [editingTimeline, setEditingTimeline] = useState(null);

  // Use Firestore data if available, fall back to hardcoded defaults
  const nodes = data.nodes || NODES;
  const timeline = data.timeline || TIMELINE;

  const saveNode = (nodeId, updates) => {
    const updated = nodes.map(n => n.id === nodeId ? { ...n, ...updates } : n);
    update({ nodes: updated });
    setEditingNode(null);
  };

  const saveTimeline = (idx, updates) => {
    const updated = timeline.map((t, i) => i === idx ? { ...t, ...updates } : t);
    update({ timeline: updated });
    setEditingTimeline(null);
  };

  return (
    <div style={{ marginBottom: 36 }}>
      {/* Moon + Cast */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 auto', padding: '16px 20px', borderRadius: 14, background: 'linear-gradient(135deg,rgba(123,79,196,.15),rgba(232,98,26,.08))', border: '1px solid rgba(123,79,196,.3)', display: 'flex', alignItems: 'center', gap: 14, minWidth: 260 }}>
          <div style={{ fontSize: 36 }}>{moon.phase}</div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 3 }}>Next Cast Window</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{moon.nextCast.name}</div>
            <div style={{ fontSize: 12, color: 'var(--orange2)', fontFamily: 'var(--mono)', fontWeight: 700 }}>in {moon.nextCast.days} day{moon.nextCast.days !== 1 ? 's' : ''}</div>
          </div>
        </div>

        {/* Timeline pills */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'center', minWidth: 200 }}>
          <div style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 2 }}>The Spell · Timeline</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {timeline.map((t, idx) => (
              <div key={idx} onClick={() => setEditingTimeline(editingTimeline === idx ? null : idx)}
                style={{ padding: '5px 12px', borderRadius: 20, background: `${t.color}15`, border: `1px solid ${editingTimeline === idx ? t.color : t.color+'33'}`, fontSize: 10, fontFamily: 'var(--mono)', fontWeight: 600, color: t.color, cursor: 'pointer', transition: 'border-color .15s' }}>
                {t.label}: {t.items[0]}
              </div>
            ))}
          </div>

          {/* Inline timeline editor */}
          {editingTimeline !== null && (() => {
            const t = timeline[editingTimeline];
            return (
              <TimelineEditor t={t} color={t.color} onSave={updates => saveTimeline(editingTimeline, updates)} onClose={() => setEditingTimeline(null)} />
            );
          })()}
        </div>
      </div>

      {/* 5 Nodes */}
      <div style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>The Five Nodes · Intentions</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
        {nodes.map(node => (
          <div key={node.id}
            onClick={() => { setExpandedNode(expandedNode === node.id ? null : node.id); setEditingNode(null); }}
            style={{ padding: '12px 14px', borderRadius: 12, background: expandedNode === node.id ? `${node.color}18` : 'var(--surface)', border: `1px solid ${expandedNode === node.id ? node.color : `${node.color}44`}`, cursor: 'pointer', transition: 'all .15s' }}>
            <div style={{ fontSize: 9, color: node.color, fontFamily: 'var(--mono)', fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>NODE {node.num}</div>
            <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 3 }}>{node.name}</div>
            <div style={{ fontSize: 9, color: 'var(--dim)', fontFamily: 'var(--mono)', lineHeight: 1.4 }}>{node.milestone}</div>
          </div>
        ))}
      </div>

      {/* Expanded node — inline editable */}
      {expandedNode && (() => {
        const node = nodes.find(n => n.id === expandedNode);
        if (!node) return null;
        if (editingNode === expandedNode) {
          return <NodeEditor node={node} onSave={updates => saveNode(node.id, updates)} onClose={() => setEditingNode(null)} />;
        }
        return (
          <div style={{ marginTop: 8, padding: '14px 18px', borderRadius: 12, background: `${node.color}0d`, border: `1px solid ${node.color}33`, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, fontSize: 13, color: 'var(--muted)', lineHeight: 1.8, fontStyle: 'italic' }}>{node.intention}</div>
            <button onClick={e => { e.stopPropagation(); setEditingNode(expandedNode); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dim)', padding: 4, flexShrink: 0, fontSize: 11, fontFamily: 'var(--mono)' }}>
              ✎ edit
            </button>
          </div>
        );
      })()}
    </div>
  );
}

// ── Node inline editor ────────────────────────────────────────
function NodeEditor({ node, onSave, onClose }) {
  const [form, setForm] = useState({ name: node.name, milestone: node.milestone, intention: node.intention });
  return (
    <div style={{ marginTop: 8, padding: '16px 18px', borderRadius: 12, background: `${node.color}0d`, border: `1px solid ${node.color}44` }}
      onClick={e => e.stopPropagation()}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 10 }}>Node Name</label>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ fontSize: 13 }} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 10 }}>Milestone</label>
          <input value={form.milestone} onChange={e => setForm(f => ({ ...f, milestone: e.target.value }))} style={{ fontSize: 12 }} />
        </div>
      </div>
      <div className="form-group" style={{ margin: '0 0 10px' }}>
        <label style={{ fontSize: 10 }}>Intention</label>
        <textarea rows={3} value={form.intention} onChange={e => setForm(f => ({ ...f, intention: e.target.value }))} style={{ fontSize: 13, lineHeight: 1.7 }} />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} className="btn btn-ghost btn-sm">Cancel</button>
        <button onClick={() => onSave(form)} className="btn btn-primary btn-sm">Save</button>
      </div>
    </div>
  );
}

// ── Timeline inline editor ────────────────────────────────────
function TimelineEditor({ t, color, onSave, onClose }) {
  const [label, setLabel] = useState(t.label);
  const [items, setItems] = useState(t.items.join('\n'));
  return (
    <div style={{ marginTop: 8, padding: '14px 16px', borderRadius: 12, background: `${color}0d`, border: `1px solid ${color}44` }}
      onClick={e => e.stopPropagation()}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginBottom: 10 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 10 }}>Label</label>
          <input value={label} onChange={e => setLabel(e.target.value)} style={{ fontSize: 12 }} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 10 }}>Goals (one per line)</label>
          <textarea rows={3} value={items} onChange={e => setItems(e.target.value)} style={{ fontSize: 12, lineHeight: 1.6 }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} className="btn btn-ghost btn-sm">Cancel</button>
        <button onClick={() => onSave({ label, items: items.split('\n').map(s => s.trim()).filter(Boolean) })} className="btn btn-primary btn-sm">Save</button>
      </div>
    </div>
  );
}

// ── Tab I: Dreamscape ─────────────────────────────────────────
function DreamscapeTab({ data, update }) {
  const canvas = Array.isArray(data.canvases?.dreamscape) ? data.canvases.dreamscape : [];
  return (
    <div>
      <div style={{ padding: '16px 0 20px' }}>
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.9, fontStyle: 'italic', maxWidth: 680, marginBottom: 6 }}>
          "I reach into the Field of Dream and ask: what is it I want this Life to be? What am I moving towards?"
        </div>
        <div style={{ fontSize: 12, color: 'var(--dim)', lineHeight: 1.7, maxWidth: 680 }}>
          Build the Tapestry here — articulate the Dream in both writing and visual expression. Drop images that make you feel the world you're sailing into. Add notes for each Node. By the end, the Dream is vividly alive on this board.
        </div>
      </div>
      <MoodBoard items={canvas} onChange={items => update({ canvases: { ...data.canvases, dreamscape: items } })} readOnly={false} showSteps={false} fillPage />
    </div>
  );
}

// ── Tab II: Sigils ────────────────────────────────────────────
// Auto-push to Tab III on every change (additive).
// "Mergence" button resets that node's zone in Tab III then re-inserts current items.
function SigilsTab({ data, update }) {
  const canvases = data.canvases || {};
  const nodes = data.nodes || NODES;

  // Push items from a node canvas into Tab III (additive — no removal)
  const autoPush = (nodeId, items, allCanvases) => {
    const current = Array.isArray(allCanvases.singularity3) ? allCanvases.singularity3 : [];
    // Add any items not already present (by id)
    const existingIds = new Set(current.map(i => i.id));
    const toAdd = items
      .filter(i => !existingIds.has(i.id))
      .map(i => ({ ...i, nodeId, _zone: nodeId }));
    if (toAdd.length === 0) return;
    update({ canvases: { ...allCanvases, singularity3: [...current, ...toAdd] } });
  };

  // Mergence: wipe this node's items from Tab III, re-insert current with default zone positions
  const mergence = (nodeId, items) => {
    const allCanvases = data.canvases || {};
    const current = Array.isArray(allCanvases.singularity3) ? allCanvases.singularity3 : [];
    const kept = current.filter(i => i.nodeId !== nodeId);
    const nodeIndex = nodes.findIndex(n => n.id === nodeId);
    // Default zone positions — 5-point pentagram layout (left→right across 2 rows)
    const ZONE_ORIGINS = [
      { x: 60,  y: 40  }, // n1 top-left area
      { x: 360, y: 40  }, // n2 top-right area
      { x: 210, y: 180 }, // n3 center
      { x: 60,  y: 320 }, // n4 bottom-left
      { x: 360, y: 320 }, // n5 bottom-right
    ];
    const origin = ZONE_ORIGINS[nodeIndex] || { x: 60 + nodeIndex * 180, y: 60 };
    const reset = items.map((item, i) => ({
      ...item,
      nodeId,
      _zone: nodeId,
      x: origin.x + (i % 3) * 140,
      y: origin.y + Math.floor(i / 3) * 120,
    }));
    update({ canvases: { ...allCanvases, singularity3: [...kept, ...reset] } });
  };

  return (
    <div>
      <div style={{ padding: '16px 0 20px' }}>
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.9, fontStyle: 'italic', maxWidth: 680, marginBottom: 6 }}>
          "The unique elements of the Dreamscape are distilled, each into its own Sigil."
        </div>
        <div style={{ fontSize: 12, color: 'var(--dim)', lineHeight: 1.7, maxWidth: 680 }}>
          One Node → One Sigil. Build each Sigil in its canvas. Items auto-flow into Tab III. Hit Mergence to reset that node's zone in the Singularity.
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {nodes.map(node => {
          const nodeCanvas = Array.isArray(canvases[`sigil_${node.id}`]) ? canvases[`sigil_${node.id}`] : [];
          return (
            <div key={node.id} style={{ border: `1px solid ${node.color}44`, borderRadius: 16, overflow: 'hidden' }}>
              {/* Node header */}
              <div style={{ padding: '10px 16px', background: `${node.color}0d`, borderBottom: `1px solid ${node.color}22`, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: node.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 900, fontFamily: 'var(--mono)', color: '#fff', flexShrink: 0 }}>{node.num}</div>
                <div style={{ fontSize: 12, fontWeight: 700, flex: 1, color: node.color }}>{node.name}</div>
                <button
                  onClick={() => mergence(node.id, nodeCanvas)}
                  style={{ padding: '4px 12px', borderRadius: 8, border: `1px solid ${node.color}66`, background: `${node.color}18`, color: node.color, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--mono)', letterSpacing: 0.5 }}>
                  ✦ Mergence
                </button>
              </div>
              <div style={{ padding: 16 }}>
                <MoodBoard
                  items={nodeCanvas}
                  onChange={items => {
                    const updated = { ...canvases, [`sigil_${node.id}`]: items };
                    update({ canvases: updated });
                    autoPush(node.id, items, updated);
                  }}
                  readOnly={false}
                  showSteps={false}
                  fillPage
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Tab III: Singularity ──────────────────────────────────────
// Freeform canvas populated by Tab II. Items are draggable, color-coded by node.
// Toggle hides/shows nodeId color indicators. Stores own copy in canvases.singularity3.
function SingularityTab({ data, update }) {
  const [showColors, setShowColors] = useState(true);
  const canvases = data.canvases || {};
  const items = Array.isArray(canvases.singularity3) ? canvases.singularity3 : [];
  const nodes = data.nodes || NODES;

  const nodeColor = (nodeId) => nodes.find(n => n.id === nodeId)?.color || 'var(--border)';
  const nodeName  = (nodeId) => nodes.find(n => n.id === nodeId)?.name  || '';

  return (
    <div>
      <div style={{ padding: '16px 0 20px' }}>
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.9, fontStyle: 'italic', maxWidth: 680, marginBottom: 6 }}>
          "All the Sigils fuse into one Form — the Singularity Substance."
        </div>
        <div style={{ fontSize: 12, color: 'var(--dim)', lineHeight: 1.7, maxWidth: 680 }}>
          Everything from Tab II flows here. Drag freely. The five nodes arrive in their zones — rearrange them into the Substance. Changes here don't affect Tab II.
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          onClick={() => setShowColors(v => !v)}
          style={{ padding: '5px 14px', borderRadius: 8, border: '1px solid var(--border)', background: showColors ? 'var(--surface2)' : 'transparent', color: showColors ? 'var(--text)' : 'var(--muted)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)', fontWeight: 600 }}>
          {showColors ? '◉ Node Colors On' : '○ Node Colors Off'}
        </button>
        {/* Legend */}
        {showColors && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {nodes.map(n => (
              <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: n.color, fontFamily: 'var(--mono)', fontWeight: 600 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: n.color }} />
                {n.num} {n.name}
              </div>
            ))}
          </div>
        )}
      </div>

      {items.length === 0 && (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--dim)', fontSize: 12, fontFamily: 'var(--mono)' }}>
          Add items to Sigil canvases in Tab II — they'll flow here automatically.
        </div>
      )}

      {/* Render MoodBoard with node color overlays */}
      <div style={{ position: 'relative' }}>
        {showColors && items.length > 0 && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, pointerEvents: 'none', zIndex: 2, display: 'flex', gap: 6, flexWrap: 'wrap', padding: '4px 0' }}>
          </div>
        )}
        <MoodBoard
          items={items}
          onChange={newItems => update({ canvases: { ...canvases, singularity3: newItems } })}
          readOnly={false}
          showSteps={false}
          fillPage
          nodeColors={showColors ? Object.fromEntries(nodes.map(n => [n.id, n.color])) : {}}
        />
      </div>

      {/* External link for Milanote/outside references */}
      <div style={{ display: 'flex', gap: 8, marginTop: 20, alignItems: 'center' }}>
        <input value={data.singularityUrl || ''} onChange={e => update({ singularityUrl: e.target.value })}
          placeholder="Link to external Singularity Substance (Milanote, image...)..."
          style={{ flex: 1, fontSize: 12, padding: '8px 12px' }} />
        {data.singularityUrl && (
          <a href={data.singularityUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <ExternalLink size={12} /> Open
          </a>
        )}
      </div>
    </div>
  );
}

// ── Tab IV: Charge ────────────────────────────────────────────
function ChargeTab({ data, update }) {
  const canvas = Array.isArray(data.canvases?.charge) ? data.canvases.charge : [];
  const chargeLog = data.chargeLog || [];
  const nodes = data.nodes || NODES;
  const today = new Date().toISOString().split('T')[0];
  const todayEntry = chargeLog.find(e => e.date === today);
  const streak = (() => {
    let s = 0;
    let d = new Date();
    while (true) {
      const key = d.toISOString().split('T')[0];
      if (chargeLog.find(e => e.date === key && e.ahssiah)) s++;
      else break;
      d.setDate(d.getDate() - 1);
    }
    return s;
  })();

  const toggleAhssiah = () => {
    const newLog = todayEntry
      ? chargeLog.map(e => e.date === today ? { ...e, ahssiah: !e.ahssiah } : e)
      : [...chargeLog, { date: today, ahssiah: true, production: false, physical: false }];
    update({ chargeLog: newLog });
  };

  const toggleField = (field) => {
    const newLog = todayEntry
      ? chargeLog.map(e => e.date === today ? { ...e, [field]: !e[field] } : e)
      : [...chargeLog, { date: today, ahssiah: false, production: false, physical: false, [field]: true }];
    update({ chargeLog: newLog });
  };

  const METHODS = [
    { key: 'ahssiah',    label: 'Training in Ahssiah',        desc: 'Daily embodied vocal practice',     color: '#a87ee8' },
    { key: 'production', label: 'Completing Music',            desc: 'Production and composition',         color: '#e8621a' },
    { key: 'physical',   label: 'Physical Embodiment',         desc: 'Movement, presence, the body',       color: '#1a9080' },
  ];

  return (
    <div>
      <div style={{ padding: '16px 0 20px' }}>
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.9, fontStyle: 'italic', maxWidth: 680, marginBottom: 6 }}>
          "Make the music with the Singularity Substance physically present in the room. Having it out while creating is part of charging."
        </div>
        <div style={{ fontSize: 12, color: 'var(--dim)', lineHeight: 1.7, maxWidth: 680 }}>
          Three core charging methods. Mark each one as you complete it today. The Substance is being charged every time you work.
        </div>
      </div>

      {/* Daily practice tracker */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', fontFamily: 'var(--mono)', letterSpacing: 1 }}>TODAY'S CHARGE</div>
          {streak > 0 && (
            <div style={{ padding: '3px 10px', borderRadius: 20, background: 'rgba(232,98,26,.15)', border: '1px solid rgba(232,98,26,.3)', fontSize: 11, color: '#e8621a', fontFamily: 'var(--mono)', fontWeight: 700 }}>
              🔥 {streak} day streak
            </div>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {METHODS.map(method => {
            const done = todayEntry?.[method.key];
            return (
              <button key={method.key} onClick={() => toggleField(method.key)}
                style={{ padding: '16px 18px', borderRadius: 14, border: `1px solid ${done ? method.color : `${method.color}33`}`, background: done ? `${method.color}18` : 'var(--surface)', cursor: 'pointer', textAlign: 'left', transition: 'all .15s' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', border: `2px solid ${done ? method.color : 'var(--border)'}`, background: done ? method.color : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}>
                    {done && <Check size={12} color="#fff" />}
                  </div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: done ? 'var(--text)' : 'var(--muted)', marginBottom: 3 }}>{method.label}</div>
                <div style={{ fontSize: 11, color: 'var(--dim)' }}>{method.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Recent log */}
      {chargeLog.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)', letterSpacing: 2, marginBottom: 8 }}>RECENT CHARGE LOG</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[...chargeLog].reverse().slice(0, 14).map(entry => {
              const total = [entry.ahssiah, entry.production, entry.physical].filter(Boolean).length;
              return (
                <div key={entry.date} style={{ padding: '4px 10px', borderRadius: 8, background: total === 3 ? 'rgba(39,174,96,.15)' : total > 0 ? 'rgba(232,98,26,.12)' : 'var(--surface3)', border: `1px solid ${total === 3 ? 'rgba(39,174,96,.3)' : total > 0 ? 'rgba(232,98,26,.2)' : 'var(--border)'}`, fontSize: 10, fontFamily: 'var(--mono)', color: total === 3 ? '#27ae60' : total > 0 ? '#e8621a' : 'var(--dim)' }}>
                  {entry.date.slice(5)} {'●'.repeat(total)}{'○'.repeat(3 - total)}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Charge canvas - visual space */}
      <div style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)', letterSpacing: 2, marginBottom: 10 }}>CHARGE SPACE · Keep the Substance present</div>
      <MoodBoard items={canvas} onChange={items => update({ canvases: { ...data.canvases, charge: items } })} readOnly={false} showSteps={false} fillPage />
    </div>
  );
}

// ── Tab V: Tesseræ ────────────────────────────────────────────
function TesseraeTab({ data, update }) {
  const releases = data.releases || DEFAULT_DATA.releases;
  const nodes = data.nodes || NODES;
  const [editRelease, setEditRelease] = useState(null);
  const [showAdd, setShowAdd] = useState(null);
  const moon = getMoonData();

  const updateRelease = (id, updates) => update({ releases: releases.map(r => r.id === id ? { ...r, ...updates } : r) });
  const addRelease = (release) => update({ releases: [...releases, { ...release, id: 'r' + Date.now() }] });

  return (
    <div>
      <div style={{ padding: '16px 0 20px' }}>
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.9, fontStyle: 'italic', maxWidth: 680, marginBottom: 6 }}>
          "Every Full Moon and New Moon, I release a single — waterfalling the album. Music entering the world is the spell going live."
        </div>
        <div style={{ fontSize: 12, color: 'var(--dim)', lineHeight: 1.7, maxWidth: 680 }}>
          Trust the operation. Obsession blocks the current. Each single's cover carries an aspect of the Singularity Substance. The Substance is the through-line. Each release is the spell casting into the subconscious of the world.
        </div>
      </div>

      {/* Next cast */}
      <div style={{ padding: '12px 18px', borderRadius: 12, background: 'linear-gradient(90deg,rgba(123,79,196,.12),rgba(232,98,26,.06))', border: '1px solid rgba(123,79,196,.25)', display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
        <div style={{ fontSize: 24 }}>🌙</div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700 }}>Next cast window: {moon.nextCast.name}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>in {moon.nextCast.days} day{moon.nextCast.days !== 1 ? 's' : ''} — plan your next release</div>
        </div>
      </div>

      {/* Release waterfall */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>Release Waterfall</div>
        <button onClick={() => setShowAdd(true)} className="btn btn-primary btn-sm"><Plus size={13} /> Add Release</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {releases.map((release, idx) => {
          const meta = STATUS_META[release.status] || STATUS_META.forming;
          const linkedNode = nodes.find(n => n.id === release.nodeId);
          return (
            <div key={release.id} style={{ display: 'flex', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 32, flexShrink: 0 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: meta.color, flexShrink: 0, marginTop: 20, zIndex: 1 }} />
                {idx < releases.length - 1 && <div style={{ width: 2, flex: 1, background: 'var(--border)', marginTop: 2 }} />}
              </div>
              <div style={{ flex: 1, padding: '14px 16px', marginBottom: 8, borderRadius: 12, background: 'var(--surface)', border: `1px solid ${meta.color}33`, transition: 'border-color .15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = meta.color}
                onMouseLeave={e => e.currentTarget.style.borderColor = `${meta.color}33`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{release.title}</div>
                      <span style={{ fontSize: 9, padding: '1px 7px', borderRadius: 20, background: 'var(--surface3)', color: 'var(--muted)', fontFamily: 'var(--mono)', textTransform: 'uppercase' }}>{release.type}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: meta.color, fontFamily: 'var(--mono)' }}>{meta.label}</span>
                      {release.castDate && <span style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)' }}>Cast: {release.castDate}</span>}
                      {linkedNode && <span style={{ fontSize: 10, color: linkedNode.color, fontFamily: 'var(--mono)' }}>Node {linkedNode.num}: {linkedNode.name}</span>}
                    </div>
                  </div>
                  <button onClick={() => setEditRelease(release)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dim)', padding: 4 }}>
                    <Edit2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editRelease && (
        <Modal onClose={() => setEditRelease(null)} title={`Edit — ${editRelease.title}`}>
          <div className="form-group"><label>Title</label><input value={editRelease.title} onChange={e => setEditRelease(r => ({ ...r, title: e.target.value }))} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group"><label>Type</label><select value={editRelease.type} onChange={e => setEditRelease(r => ({ ...r, type: e.target.value }))}><option value="single">Single</option><option value="ep">EP</option><option value="album">Album</option></select></div>
            <div className="form-group"><label>Status</label><select value={editRelease.status} onChange={e => setEditRelease(r => ({ ...r, status: e.target.value }))}>{Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
            <div className="form-group"><label>Cast Date</label><input type="date" value={editRelease.castDate || ''} onChange={e => setEditRelease(r => ({ ...r, castDate: e.target.value }))} /></div>
            <div className="form-group"><label>Linked Node</label><select value={editRelease.nodeId || ''} onChange={e => setEditRelease(r => ({ ...r, nodeId: e.target.value }))}><option value="">None</option>{nodes.map(n => <option key={n.id} value={n.id}>Node {n.num}: {n.name}</option>)}</select></div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setEditRelease(null)} className="btn btn-ghost">Cancel</button>
            <button onClick={() => { updateRelease(editRelease.id, editRelease); setEditRelease(null); }} className="btn btn-primary">Save</button>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)} title="Add Release">
          <ReleaseForm onSave={r => { addRelease(r); setShowAdd(false); }} onCancel={() => setShowAdd(false)} nodes={nodes} />
        </Modal>
      )}
    </div>
  );
}

function ReleaseForm({ onSave, onCancel, nodes = NODES }) {
  const [form, setForm] = useState({ title: '', type: 'single', status: 'forming', castDate: '', nodeId: '' });
  return (
    <>
      <div className="form-group"><label>Title</label><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Song or project title..." autoFocus /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="form-group"><label>Type</label><select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}><option value="single">Single</option><option value="ep">EP</option><option value="album">Album</option></select></div>
        <div className="form-group"><label>Status</label><select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>{Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
        <div className="form-group"><label>Cast Date</label><input type="date" value={form.castDate} onChange={e => setForm(f => ({ ...f, castDate: e.target.value }))} /></div>
        <div className="form-group"><label>Linked Node</label><select value={form.nodeId} onChange={e => setForm(f => ({ ...f, nodeId: e.target.value }))}><option value="">None</option>{nodes.map(n => <option key={n.id} value={n.id}>Node {n.num}: {n.name}</option>)}</select></div>
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} className="btn btn-ghost">Cancel</button>
        <button onClick={() => { if (form.title.trim()) onSave(form); }} disabled={!form.title.trim()} className="btn btn-primary">✦ Add to Waterfall</button>
      </div>
    </>
  );
}

// ── Architecture tabs config ──────────────────────────────────
const ARCH_TABS = [
  { id: 'dreamscape',  roman: 'I',   label: 'Draping Dreamscape',            color: '#1a9080', Component: DreamscapeTab },
  { id: 'sigils',      roman: 'II',  label: 'Distill — Sigils',              color: '#7b4fc4', Component: SigilsTab },
  { id: 'singularity', roman: 'III', label: 'Mergence — Singularity',        color: '#c98a18', Component: SingularityTab },
  { id: 'charge',      roman: 'IV',  label: 'Charge',                        color: '#e8621a', Component: ChargeTab },
  { id: 'tess',        roman: 'V',   label: 'Tesseræ {Cast}',                color: '#a87ee8', Component: TesseraeTab },
];

// ── Main page ─────────────────────────────────────────────────
export default function Hypersigil({ activeArtistId }) {
  const { profile, isManager } = useAuth();

  // Map sidebar artistId to hypersigil document key
  // The sidebar uses Firestore artist doc IDs — map them to our hypersigil keys
  const resolveHypersigId = (id) => {
    if (!id) return 'avidx'; // null = "All" = manager's own
    if (id === 'ahzira' || id === 'ahzira_v2') return 'ahzira';
    if (id === 'avidx') return 'avidx';
    // Fallback: check profile email
    return profile?.email === 'aiahzira@gmail.com' ? 'ahzira' : 'avidx';
  };

  const ownArtistId = profile?.email === 'aiahzira@gmail.com' ? 'ahzira' : 'avidx';
  const viewingArtistId = resolveHypersigId(activeArtistId);
  const artistName = viewingArtistId === 'ahzira' ? 'AhziRa' : 'Avidx';

  const { data, update } = useHypersigil(viewingArtistId);
  const [activeTab, setActiveTab] = useState('dreamscape');
  const [showPrivacyMenu, setShowPrivacyMenu] = useState(false);

  const isOwn = viewingArtistId === ownArtistId;
  const isPrivate = data?.isPrivate || false;

  if (!data) return (
    <div style={{ padding: 32, color: 'var(--muted)', position: 'relative', zIndex: 1, fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: 2 }}>
      INITIALIZING HYPERSIGIL...
    </div>
  );

  // Block view if private and not owner
  if (isPrivate && !isOwn) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', position: 'relative', zIndex: 1 }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>This Hypersigil is private</div>
      <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--dim)' }}>{artistName} has marked their Hypersigil private.</div>
    </div>
  );

  const activeTabConfig = ARCH_TABS.find(t => t.id === activeTab);
  const { Component } = activeTabConfig;

  return (
    <div style={{ position: 'relative', zIndex: 1 }}>

      {/* ── Always-on top header ── */}
      <div style={{ padding: '24px 30px 0', borderBottom: '1px solid var(--border)' }}>
        {/* Title row */}
        <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 6 }}>
              Classified Operational Document · Creature Records
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 900, letterSpacing: -0.5, marginBottom: 6 }}>
              {artistName} — <span style={{ background: 'linear-gradient(135deg,#e8621a,#7b4fc4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Hypersigil</span>
            </h1>
            <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic', lineHeight: 1.6, maxWidth: 600 }}>
              "Pair a hyperfocused intention with an elevated state of emotion and you will change reality." <span style={{ fontStyle: 'normal', color: 'var(--dim)' }}>— Dr. Joe Dispenza</span>
            </div>
          </div>

          {/* Controls — privacy toggle only */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>

            {/* Privacy toggle — only for own hypersigil */}
            {isOwn && (
              <div style={{ position: 'relative' }}>
                <button onClick={() => setShowPrivacyMenu(v => !v)}
                  style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${isPrivate ? 'rgba(192,57,43,.4)' : 'var(--border)'}`, background: isPrivate ? 'rgba(192,57,43,.1)' : 'transparent', color: isPrivate ? '#e74c3c' : 'var(--muted)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                  {isPrivate ? '🔒 Private' : '👁 Visible'}
                </button>
                {showPrivacyMenu && (
                  <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 8, minWidth: 180, zIndex: 100 }}>
                    <button onClick={() => { update({ isPrivate: false }); setShowPrivacyMenu(false); }}
                      style={{ width: '100%', padding: '8px 12px', textAlign: 'left', background: !isPrivate ? 'var(--surface2)' : 'none', border: 'none', cursor: 'pointer', borderRadius: 6, fontSize: 12, color: 'var(--text)', fontFamily: 'var(--font)' }}>
                      👁 Visible to manager
                    </button>
                    <button onClick={() => { update({ isPrivate: true }); setShowPrivacyMenu(false); }}
                      style={{ width: '100%', padding: '8px 12px', textAlign: 'left', background: isPrivate ? 'rgba(192,57,43,.1)' : 'none', border: 'none', cursor: 'pointer', borderRadius: 6, fontSize: 12, color: isPrivate ? '#e74c3c' : 'var(--text)', fontFamily: 'var(--font)' }}>
                      🔒 Private (only me)
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Intentions dashboard */}
        <IntentionsDashboard data={data} update={update} />

        {/* Architecture tabs */}
        <div style={{ display: 'flex', gap: 0, overflowX: 'auto' }}>
          {ARCH_TABS.map(tab => {
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 20px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                background: isActive ? 'var(--surface)' : 'transparent',
                borderTop: `2px solid ${isActive ? tab.color : 'transparent'}`,
                borderBottom: `1px solid ${isActive ? 'var(--surface)' : 'transparent'}`,
                marginBottom: isActive ? -1 : 0,
                transition: 'all .15s',
              }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                  background: isActive ? tab.color : 'var(--surface3)',
                  border: `1px solid ${isActive ? tab.color : 'var(--border)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 9, fontWeight: 900, fontFamily: 'var(--mono)',
                  color: isActive ? '#fff' : 'var(--dim)', transition: 'all .15s',
                }}>{tab.roman}</div>
                <span style={{ fontSize: 12, fontWeight: isActive ? 700 : 500, color: isActive ? 'var(--text)' : 'var(--muted)' }}>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Active tab content ── */}
      <div style={{ padding: '0 30px 40px' }}>
        <Component data={data} update={update} />
      </div>
    </div>
  );
}
